import React, { useState, useEffect } from "react";
import { Pencil, Edit } from 'lucide-react';
import apiService from '../../../config';

const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        #printable-area, #printable-area * {
            visibility: visible;
        }
        #printable-area {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            padding: 20px;
        }
        .no-print {
            display: none !important;
        }
    }
`;

const Parishtisht_ASubmissionLetter = () => {
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);
    const [currentProjectName, setCurrentProjectName] = useState(null);
    const [tableRows, setTableRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Edit state per row index: { value, saving, error }
    const [editState, setEditState] = useState({});

    const handlePrint = () => {
        window.print();
    };

    // Project Id
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return { id: projectData.project_id || projectData.id, name: projectData.project_name || projectData.name };
                } catch (e) { console.error('Error parsing currentProjectData:', e); }
            }
            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return { id: project.project_id || project.id, name: project.project_name || project.name };
                } catch (e) { console.error('Error parsing selectedProject:', e); }
            }
            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return {
                        id: user.data?.project_id || user.project_id,
                        name: user.data?.project_name || user.project_name || 'Unknown Project'
                    };
                } catch (e) { console.error('Error parsing userData:', e); }
            }
            return { id: null, name: null };
        };
        const projectInfo = getProjectInfo();
        setCurrentProjectId(projectInfo.id);
        setCurrentProjectName(projectInfo.name);
    }, []);

    // Proposal id
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id ||
                            null
                    };
                } catch (e) { console.error('Error parsing getProposalInfo:', e); }
            }
            return { proposalId: null };
        };
        const proposalInfo = getProposalInfo();
        setCurrentProposalId(proposalInfo.proposalId);
    }, []);

    // Fetch API data
    useEffect(() => {
        if (!currentProjectId || !currentProposalId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiService.getRailwayLandHolding(currentProjectId, {
                    proposalId: currentProposalId
                });

                const landData = response?.data || [];

                const rows = landData.map((parcel, index) => {
                    const owners = parcel.owners || [];
                    const totalOwnerArea = owners.reduce((sum, o) => sum + (parseFloat(o.owner_total_area) || 0), 0);
                    const totalAcquiredArea = owners.reduce((sum, o) => sum + (parseFloat(o.owner_aqr_area) || 0), 0);

                    return {
                        sr: index + 1,
                        landId: parcel.LandAcquisition_id,  // used for PATCH
                        village: parcel.village_name || "-",
                        surveyNo: parcel.survey_no || "-",
                        totalArea: totalOwnerArea.toFixed(2),
                        acquiredArea: totalAcquiredArea.toFixed(2),
                        remark: parcel.parishist_A_remarks || ""
                    };
                });

                setTableRows(rows);
            } catch (err) {
                console.error('Error fetching land holding data:', err);
                setError('माहिती लोड करताना त्रुटी आली.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentProjectId, currentProposalId]);

    // Open inline edit for a row
    const handleEditClick = (index, currentRemark) => {
        setEditState(prev => ({
            ...prev,
            [index]: { value: currentRemark || "", saving: false, error: null }
        }));
    };

    // Cancel edit
    const handleCancelEdit = (index) => {
        setEditState(prev => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    // Save remark via PATCH
    const handleSaveRemark = async (index) => {
        const row = tableRows[index];
        const landId = row.landId;
        const newRemark = editState[index]?.value ?? "";

        if (!landId) {
            setEditState(prev => ({
                ...prev,
                [index]: { ...prev[index], error: 'Land ID उपलब्ध नाही.' }
            }));
            return;
        }

        setEditState(prev => ({
            ...prev,
            [index]: { ...prev[index], saving: true, error: null }
        }));

        try {
            await apiService.updateRailwayLandHolding(landId, {
                parishist_A_remarks: newRemark
            });

            // Update local row on success
            setTableRows(prev => prev.map((r, i) =>
                i === index ? { ...r, remark: newRemark } : r
            ));

            // Close edit
            setEditState(prev => {
                const next = { ...prev };
                delete next[index];
                return next;
            });
        } catch (err) {
            console.error('Error saving remark:', err);
            setEditState(prev => ({
                ...prev,
                [index]: { ...prev[index], saving: false, error: 'जतन करताना त्रुटी आली.' }
            }));
        }
    };

    // Grand total
    const grandTotalAcquired = tableRows
        .reduce((sum, row) => sum + (parseFloat(row.acquiredArea) || 0), 0)
        .toFixed(2);

    return (
        <>
            <style>{printStyles}</style>

            <div className="bg-white text-black p-6 relative">
                {/* Print Button */}
                <button
                    onClick={handlePrint}
                    className="no-print absolute right-6 top-6 px-4 py-2 text-sm font-semibold text-white rounded shadow-md"
                    style={{ backgroundColor: '#16a34a' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#15803d'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#16a34a'}
                >
                    🖨️ Print
                </button>

                {/* Printable Area */}
                <div id="printable-area">
                    {/* Heading */}
                    <div className="mt-10 text-center">
                        <h2 className="font-semibold">परिशिष्ट 'अ'</h2>
                        <p className="mt-1 text-sm">
                            खाजगी जमिनी संपादन प्रस्ताव क्षेत्राच्या संबंधित माहिती दर्शविणारा तक्ता
                        </p>
                    </div>

                    {/* Loading / Error States */}
                    {loading && (
                        <div className="mt-6 text-center text-sm text-gray-500">माहिती लोड होत आहे...</div>
                    )}
                    {error && (
                        <div className="mt-6 text-center text-sm text-red-500">{error}</div>
                    )}

                    {/* Table */}
                    {!loading && !error && (
                        <div className="mt-6">
                            <table className="w-full border border-black border-collapse text-sm">
                                <thead>
                                    <tr>
                                        <th className="border border-black p-2">अ. क्र.</th>
                                        <th className="border border-black p-2">गावाचे नाव</th>
                                        <th className="border border-black p-2">सर्व्हे नंबर</th>
                                        <th className="border border-black p-2">
                                            एकूण क्षेत्र <br />(हेक्टर-आर-गुंठे)
                                        </th>
                                        <th className="border border-black p-2">
                                            संपादन करावयाचे क्षेत्र <br />(हेक्टर-आर-गुंठे)
                                        </th>
                                        <th className="border border-black p-2">शेरा</th>
                                        <th className="border border-black p-2 no-print">क्रिया</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {tableRows.length > 0 ? (
                                        tableRows.map((row, index) => {
                                            const isEditing = editState.hasOwnProperty(index);
                                            const es = editState[index] || {};

                                            return (
                                                <tr key={index}>
                                                    <td className="border border-black p-2 text-center">{row.sr}</td>
                                                    <td className="border border-black p-2">{row.village}</td>
                                                    <td className="border border-black p-2 text-center">{row.surveyNo}</td>
                                                    <td className="border border-black p-2 text-center">{row.totalArea}</td>
                                                    <td className="border border-black p-2 text-center">{row.acquiredArea}</td>

                                                    {/* शेरा cell */}
                                                    <td className="border border-black p-2 min-w-32">
                                                        {isEditing ? (
                                                            <div className="flex flex-col gap-1">
                                                                <input
                                                                    type="text"
                                                                    value={es.value}
                                                                    onChange={e => setEditState(prev => ({
                                                                        ...prev,
                                                                        [index]: { ...prev[index], value: e.target.value }
                                                                    }))}
                                                                    className="border border-gray-400 rounded px-2 py-1 text-sm w-full focus:outline-none focus:border-blue-500"
                                                                    placeholder="शेरा टाका..."
                                                                    disabled={es.saving}
                                                                    autoFocus
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') handleSaveRemark(index);
                                                                        if (e.key === 'Escape') handleCancelEdit(index);
                                                                    }}
                                                                />
                                                                {es.error && (
                                                                    <span className="text-xs text-red-500">{es.error}</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span>{row.remark || "-"}</span>
                                                        )}
                                                    </td>

                                                    {/* Action cell — hidden on print */}
                                                    <td className="border border-black p-2 text-center no-print">
                                                        {isEditing ? (
                                                            <div className="flex gap-1 justify-center">
                                                                <button
                                                                    onClick={() => handleSaveRemark(index)}
                                                                    disabled={es.saving}
                                                                    className="px-2 py-1 text-xs text-white rounded font-semibold"
                                                                    style={{ backgroundColor: es.saving ? '#9ca3af' : '#16a34a', minWidth: 32 }}
                                                                    title="जतन करा"
                                                                >
                                                                    {es.saving ? '...' : '✓'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleCancelEdit(index)}
                                                                    disabled={es.saving}
                                                                    className="px-2 py-1 text-xs text-white rounded font-semibold"
                                                                    style={{ backgroundColor: '#ef4444', minWidth: 32 }}
                                                                    title="रद्द करा"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleEditClick(index, row.remark)}
                                                                className="px-2 py-1 text-xs text-white rounded"
                                                                style={{ backgroundColor: '#3b82f6' }}
                                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2563eb'}
                                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#3b82f6'}
                                                                title="शेरा संपादित करा"
                                                            >
                                                                <Pencil/>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="border border-black p-2 text-center text-gray-400">
                                                माहिती उपलब्ध नाही
                                            </td>
                                        </tr>
                                    )}

                                    {tableRows.length > 0 && (
                                        <tr>
                                            <td colSpan="4" className="border border-black p-2 text-right font-semibold">
                                                एकूण
                                            </td>
                                            <td className="border border-black p-2 text-center font-semibold">
                                                {grandTotalAcquired}
                                            </td>
                                            <td className="border border-black p-2"></td>
                                            <td className="border border-black p-2 no-print"></td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="mt-10 text-center text-sm">
                        <p>
                            उपकार्यकारी अभियंता, (बांधकाम विभाग), <br />
                            दक्षिण मध्य रेल्वे, अकोला
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Parishtisht_ASubmissionLetter;